import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, Mic2, Volume2, PhoneCall, Check } from "lucide-react";
import { toast } from "sonner";

const VoiceSettingsCard = ({ API, getAuthConfig }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [voices, setVoices] = useState([]);
  const [currentVoice, setCurrentVoice] = useState("Polly.Joanna-Neural");
  const [savedVoice, setSavedVoice] = useState("Polly.Joanna-Neural");
  const [previewText, setPreviewText] = useState(
    "Hi! This is a preview of your new IVR voice. Your callers will hear this voice when they call your business. Thanks!"
  );
  const [previewTo, setPreviewTo] = useState("");

  useEffect(() => {
    axios.get(`${API}/settings/voice`, getAuthConfig())
      .then((r) => {
        setVoices(r.data.voices || []);
        setCurrentVoice(r.data.voice);
        setSavedVoice(r.data.voice);
      })
      .catch(() => toast.error("Failed to load voice settings"))
      .finally(() => setLoading(false));
  }, [API, getAuthConfig]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/voice`, { voice: currentVoice }, getAuthConfig());
      setSavedVoice(currentVoice);
      toast.success("Voice updated — all new calls will use this voice");
    } catch {
      toast.error("Failed to save voice");
    } finally {
      setSaving(false);
    }
  };

  const previewCall = async () => {
    if (!previewTo.trim()) {
      toast.error("Enter a phone number to receive the preview call");
      return;
    }
    setPreviewing(true);
    try {
      await axios.post(`${API}/settings/voice/preview`,
        { voice: currentVoice, text: previewText, to_number: previewTo.trim() },
        getAuthConfig()
      );
      toast.success(`Calling ${previewTo.trim()} now with a preview of ${pickName(voices, currentVoice)}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to place preview call");
    } finally {
      setPreviewing(false);
    }
  };

  const selected = voices.find((v) => v.id === currentVoice);
  const hasUnsaved = currentVoice !== savedVoice;

  return (
    <Card className="glass-card border-theme" data-testid="voice-settings-card">
      <CardHeader>
        <CardTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
          <Mic2 className="w-5 h-5 text-emerald-500" /> IVR &amp; Voicemail Voice
        </CardTitle>
        <CardDescription className="text-muted-foreground">
          Pick the voice your callers hear on IVR greetings, menu prompts, hold messages, and voicemail.
          Applies to all your phone numbers. Uses high-quality Amazon Polly Neural voices.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <div>
              <Label className="text-muted-foreground text-xs uppercase tracking-wider">Voice</Label>
              <Select value={currentVoice} onValueChange={setCurrentVoice}>
                <SelectTrigger className="mt-1" data-testid="voice-select-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {voices.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      <div className="flex items-center gap-2">
                        <Volume2 className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="font-medium">{v.name}</span>
                        <span className="text-xs text-muted-foreground">· {v.accent} · {v.gender}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selected?.description && (
                <p className="text-xs text-muted-foreground mt-2">{selected.description}</p>
              )}
            </div>

            {/* Preview via real call */}
            <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <PhoneCall className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-semibold text-foreground">Preview this voice</span>
              </div>
              <p className="text-xs text-muted-foreground">
                We'll place a short call to your phone so you can hear the exact Polly voice your callers will hear.
              </p>
              <div>
                <Label className="text-muted-foreground text-xs">Phone number to call</Label>
                <Input
                  value={previewTo}
                  onChange={(e) => setPreviewTo(e.target.value)}
                  placeholder="+1 555 000 1234"
                  className="mt-1"
                  data-testid="voice-preview-to"
                />
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Sample message</Label>
                <Input
                  value={previewText}
                  onChange={(e) => setPreviewText(e.target.value.slice(0, 240))}
                  className="mt-1"
                  data-testid="voice-preview-text"
                />
              </div>
              <Button
                onClick={previewCall}
                disabled={previewing || !previewTo.trim()}
                variant="outline"
                data-testid="voice-preview-btn"
              >
                {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PhoneCall className="w-4 h-4 mr-2" />}
                Call me now
              </Button>
            </div>

            <div className="flex items-center justify-end gap-2">
              {!hasUnsaved && (
                <span className="text-xs text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                  <Check className="w-3 h-3" /> Saved
                </span>
              )}
              <Button
                onClick={save}
                disabled={!hasUnsaved || saving}
                className="bg-emerald-600 text-white hover:bg-emerald-600/90"
                data-testid="voice-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Voice
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

function pickName(voices, id) {
  const v = voices.find((x) => x.id === id);
  return v ? `${v.name} (${v.accent})` : id;
}

export default VoiceSettingsCard;
